import type { Avatar as AvatarData } from '@/domain/records';
import { cn } from '@/lib/cn';

/** Locally generated avatars: an animal character on a colour. No photos, ever. */
export const AVATAR_CHARACTERS = [
  { id: 'owl', emoji: '🦉' },
  { id: 'cat', emoji: '🐱' },
  { id: 'lion', emoji: '🦁' },
  { id: 'turtle', emoji: '🐢' },
  { id: 'fish', emoji: '🐠' },
  { id: 'bee', emoji: '🐝' },
  { id: 'rabbit', emoji: '🐰' },
  { id: 'fox', emoji: '🦊' },
] as const;

export const AVATAR_COLORS = {
  mint: 'bg-brand-100',
  sky: 'bg-sky-100',
  sun: 'bg-sun-100',
  coral: 'bg-coral-100',
  plum: 'bg-plum-100',
} as const;

export type AvatarCharacterId = (typeof AVATAR_CHARACTERS)[number]['id'];

const sizes = {
  sm: 'size-10 text-2xl',
  md: 'size-14 text-3xl',
  lg: 'size-20 text-5xl',
  xl: 'size-28 text-7xl',
};

export function Avatar({
  avatar,
  size = 'md',
  className,
  label,
}: {
  avatar: AvatarData;
  size?: keyof typeof sizes;
  className?: string;
  label?: string;
}) {
  const character =
    AVATAR_CHARACTERS.find((c) => c.id === avatar.character) ?? AVATAR_CHARACTERS[0];
  const color = AVATAR_COLORS[avatar.color as keyof typeof AVATAR_COLORS] ?? AVATAR_COLORS.mint;
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn(
        'emoji inline-flex shrink-0 items-center justify-center rounded-full',
        color,
        sizes[size],
        className,
      )}
    >
      {character.emoji}
    </span>
  );
}
